import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { renderLabel, parseMg, doseUnits, fmtShort } from "@/lib/label";
import { niimbotSupported, printToNiimbot } from "@/lib/niimbot";

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

function initialForm(o, it, discardDays) {
  const mixed = it.recon_done_at ? new Date(it.recon_done_at) : new Date();
  const [, dose = "", unit = "mcg"] = /^([\d.]+)\s*(mcg|mg)?/i.exec(it.label_dose || "") || [];
  return {
    client: o.client_name, peptide: it.name, strength: it.vial_size || "",
    bac_ml: it.bac_ml || "", dose, unit: unit.toLowerCase(),
    mixed: mixed.toLocaleDateString("en-CA"), discard_days: discardDays, order: o.order_number,
  };
}

function buildData(f) {
  const mg = parseMg(f.strength);
  const bac = Number(f.bac_ml);
  const conc = mg && bac ? Math.round((mg / bac) * 100) / 100 : null;
  const units = doseUnits(mg, bac, f.dose, f.unit);
  const mixed = new Date(`${f.mixed}T12:00:00`);
  const discard = new Date(mixed.getTime() + Number(f.discard_days || 0) * 86400000);
  return {
    client: f.client, peptide: f.peptide, strength: f.strength,
    mixLine: bac ? `${bac} mL BAC water${conc ? ` · ${conc} mg/mL` : ""}` : "",
    doseLine: f.dose ? `${f.dose} ${f.unit}${units ? ` = ${units} units (U-100)` : ""}` : "",
    footer: [`Mixed ${fmtShort(mixed)}`, Number(f.discard_days) > 0 && `Discard ${fmtShort(discard)}`, f.order, "Refrigerate"].filter(Boolean).join(" · "),
    units,
  };
}

export function LabelDialog({ target, onClose }) {
  const canvasRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [f, setF] = useState(null);
  const [rotate, setRotate] = useState(false);
  const [copies, setCopies] = useState(1);
  const [density, setDensity] = useState("3");
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!target) {
      setOrder(null);
      setF(null);
      return;
    }
    Promise.all([api.get(`/admin/orders/${target.orderId}`), api.get("/admin/settings")]).then(([o, s]) => {
      const it = o.data.items[target.itemIndex];
      setOrder(o.data);
      setCopies(it.quantity);
      setF(initialForm(o.data, it, s.data.label_discard_days ?? 28));
    }).catch((e) => toast.error(errMsg(e)));
  }, [target]);

  const data = useMemo(() => (f ? buildData(f) : null), [f]);

  useEffect(() => {
    if (data && canvasRef.current) {
      document.fonts?.ready.then(() => renderLabel(canvasRef.current, data, rotate));
    }
  }, [data, rotate]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const saveDose = () => {
    const dose = f.dose ? `${f.dose} ${f.unit}` : "";
    api.patch(`/admin/orders/${order.id}/items/${target.itemIndex}/label`, { dose }).catch((e) => toast.error(`Dose not saved: ${errMsg(e)}`));
  };

  const download = () => {
    saveDose();
    const a = document.createElement("a");
    a.href = canvasRef.current.toDataURL("image/png");
    a.download = `${f.order}-${f.peptide}-label.png`.replace(/\s+/g, "_");
    a.click();
  };

  const print = async () => {
    if (!niimbotSupported()) {
      toast.error("Printing needs Chrome or Edge on a computer or Android phone. Use Download PNG instead.");
      return;
    }
    setBusy("Connecting…");
    saveDose();
    try {
      await printToNiimbot(canvasRef.current.toDataURL("image/png"), {
        width: canvasRef.current.width, height: canvasRef.current.height, copies: Number(copies) || 1,
        density: Number(density), showAll, onProgress: () => setBusy("Printing…"),
      });
      toast.success(`Printed ${copies} label${copies > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.message || "Print failed — check the printer is on and nearby");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="label-dialog">
        <DialogHeader><DialogTitle>Vial label · 40 × 20 mm</DialogTitle></DialogHeader>
        {!f ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <div className="grid place-items-center rounded-md border border-border bg-white p-3">
                <canvas ref={canvasRef} data-testid="label-preview-canvas" className="h-auto max-h-64 w-full max-w-[340px] object-contain" />
              </div>
              {data.units && <p className="font-mono-data text-xs text-emerald-400" data-testid="label-units-hint">Draw to {data.units} units on a U-100 insulin syringe</p>}
              <label className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
                Rotate 90° (label feeds sideways)
                <Switch checked={rotate} onCheckedChange={setRotate} data-testid="label-rotate-switch" />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><F label="Client name"><Input value={f.client} onChange={set("client")} data-testid="label-client-input" /></F></div>
              <F label="Peptide"><Input value={f.peptide} onChange={set("peptide")} data-testid="label-peptide-input" /></F>
              <F label="Strength (e.g. 5 mg)"><Input value={f.strength} onChange={set("strength")} data-testid="label-strength-input" /></F>
              <F label="BAC water (mL)"><Input type="number" step="0.1" value={f.bac_ml} onChange={set("bac_ml")} data-testid="label-bac-input" /></F>
              <F label="Amount per draw">
                <div className="flex gap-1">
                  <Input type="number" step="any" value={f.dose} onChange={set("dose")} placeholder="250" data-testid="label-dose-input" />
                  <Select value={f.unit} onValueChange={(v) => setF({ ...f, unit: v })}>
                    <SelectTrigger className="w-20" data-testid="label-dose-unit-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="mcg">mcg</SelectItem><SelectItem value="mg">mg</SelectItem></SelectContent>
                  </Select>
                </div>
              </F>
              <F label="Mixed on"><Input type="date" value={f.mixed} onChange={set("mixed")} data-testid="label-mixed-input" /></F>
              <F label="Discard after (days)"><Input type="number" min="0" value={f.discard_days} onChange={set("discard_days")} data-testid="label-discard-input" /></F>
              <F label="Copies"><Input type="number" min="1" max="50" value={copies} onChange={(e) => setCopies(e.target.value)} data-testid="label-copies-input" /></F>
              <F label="Darkness">
                <Select value={density} onValueChange={setDensity}>
                  <SelectTrigger data-testid="label-density-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["1", "2", "3", "4", "5"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <label className="col-span-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={showAll} onCheckedChange={setShowAll} data-testid="label-show-all-switch" />
                Printer not in the list? Show all Bluetooth devices
              </label>
              <div className="col-span-2 flex flex-wrap gap-2 pt-2">
                <Button onClick={print} disabled={!!busy} className="gap-2" data-testid="label-print-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}{busy || "Print to Niimbot"}
                </Button>
                <Button variant="outline" onClick={download} className="gap-2" data-testid="label-download-button"><Download className="h-4 w-4" />Download PNG</Button>
              </div>
              <p className="col-span-2 text-xs text-muted-foreground">Turn the M2 on and keep it close. Your browser asks you to pick the printer the first time.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
